import { Code, Paper, Stack, Text } from '@mantine/core'
import type { ToolCallProps, QuestionItem } from '@/types'
import { getToolDisplayName } from '@/lib/toolNames'
import QuestionMessage from '@/components/QuestionMessage'

type ToolCallGroupProps = {
  calls: ToolCallProps[]
  onResume: (body: Record<string, unknown>) => Promise<void>
}

function SingleToolCall({ toolCall }: { toolCall: ToolCallProps }) {
  const hasArguments = Object.keys(toolCall.arguments).length > 0

  return (
    <details>
      <summary>
        {getToolDisplayName(toolCall.name)}
      </summary>
      {hasArguments && (
        <Code block mt="sm">
          {JSON.stringify(toolCall.arguments, null, 2)}
        </Code>
      )}
    </details>
  )
}

function renderQuestionSummary(toolCall: ToolCallProps, questions: QuestionItem[]) {
  const headers = questions.map((q) => q.header).join(', ')
  const status = toolCall.result?.status

  if (status === 'dismissed') {
    return `Dismissed: ${headers}`
  }

  if (status === 'answered') {
    return `Answered: ${headers}`
  }

  return `Asked: ${headers}`
}

export default function ToolCallGroup({ calls, onResume }: ToolCallGroupProps) {
  // Check if this group contains a question tool call — render it specially
  const questionCall = calls.find((tc) => tc.name === 'question' && tc.arguments.questions)
  if (questionCall) {
    const questions = questionCall.arguments.questions as QuestionItem[]
    const questionStatus = questionCall.result?.status

    if (questionStatus === 'answered' || questionStatus === 'dismissed') {
      return (
        <Paper p="md" radius="lg" style={{ maxWidth: 760 }}>
          <Text c="dimmed" size="sm">
            {renderQuestionSummary(questionCall, questions)}
          </Text>
        </Paper>
      )
    }

    return (
      <QuestionMessage
        toolCallId={questionCall.tool_call_id}
        questions={questions}
        onResume={onResume}
      />
    )
  }

  // Single tool call — render inline (no extra collapse wrapper)
  if (calls.length === 1) {
    return (
      <Paper p="md" radius="lg" style={{ maxWidth: 760 }}>
        <Text fw={500} size="sm" mb="xs">Tool call</Text>
          <SingleToolCall toolCall={calls[0]} />
      </Paper>
    )
  }

  // Multiple tool calls — render collapsed with count
  return (
    <Paper p="md" radius="lg" style={{ maxWidth: 760 }}>
      <Text fw={500} size="sm" mb="xs">{calls.length} tool calls</Text>
        <details>
          <summary>Expand details</summary>
          <Stack gap="sm" mt="sm">
            {calls.map((tc, j) => (
              <Paper key={j} p="sm" withBorder>
                <SingleToolCall toolCall={tc} />
              </Paper>
            ))}
          </Stack>
        </details>
    </Paper>
  )
}
